import { validateTicketGraph } from '../domain/merge';
import type { CoupleSpace, Ticket, TicketEvent } from '../domain/types';
import { fromBase64Url, toBase64Url } from '../protocol/base64url';
import { deriveBackupKey } from '../protocol/crypto';
import type {
  AppRepositoryContract,
  BackupImportResult,
  BackupRepositoryContract,
  BackupSnapshot,
} from './repository';

export interface BackupFileV1 {
  format: 'two-little-tickets-backup';
  version: 1;
  salt: string;
  iv: string;
  ciphertext: string;
  createdAt: string;
}

export interface BackupPreview {
  spaceName: string;
  ticketCount: number;
  createdAt: string;
}

export const MAX_BACKUP_FILE_BYTES = 32 * 1024 * 1024;

export { type BackupImportResult } from './repository';

export class BackupError extends Error {
  constructor(message: string, readonly code: string) {
    super(message);
    this.name = 'BackupError';
  }
}

interface BackupPayloadV1 {
  format: 'two-little-tickets-data';
  version: 1;
  createdAt: string;
  space: CoupleSpace;
  tickets: Ticket[];
  images: Record<string, string>;
}

const ENVELOPE_KEYS = ['format', 'version', 'salt', 'iv', 'ciphertext', 'createdAt'] as const;
const PAYLOAD_KEYS = ['format', 'version', 'createdAt', 'space', 'tickets', 'images'] as const;
const REQUIRED_SPACE_KEYS = [
  'coupleId', 'memberId', 'spaceName', 'selfName', 'selfAvatar', 'partnerName', 'partnerAvatar',
  'anniversary', 'key', 'createdAt',
] as const;
const SPACE_KEYS = [...REQUIRED_SPACE_KEYS, 'peerReadyAt'] as const;
const TICKET_KEYS = [
  'type', 'title', 'description', 'outcome', 'dueDate', 'importance', 'tags', 'signature', 'imageId',
  'id', 'coupleId', 'creatorId', 'recipientId', 'createdAt', 'updatedAt', 'state', 'headEventId', 'events',
] as const;
const EVENT_KEYS = ['id', 'parentIds', 'actor', 'action', 'at', 'note', 'state'] as const;
const MEMBERS = new Set(['creator', 'partner']);
const STATES = new Set(['pending_response', 'accepted', 'rejected', 'pending_confirmation', 'completed']);
const ACTIONS = new Set(['created', 'accepted', 'rejected', 'submitted', 'confirmed', 'conflict_resolved']);
const TYPES = new Set(['reward', 'penalty']);
const IMPORTANCE = new Set(['small', 'promise', 'important']);

export async function exportBackup(repo: AppRepositoryContract, passphrase: string): Promise<Blob> {
  requirePassphrase(passphrase);
  const [space, tickets] = await Promise.all([repo.getSpace(), repo.listTickets()]);
  if (!space) throw new BackupError('no local couple space to back up', 'BACKUP_EMPTY');
  validateSpace(space);
  for (const ticket of tickets) validateTicket(ticket);

  const images: Record<string, string> = {};
  const imageIds = new Set(tickets.flatMap((ticket) => ticket.imageId ? [ticket.imageId] : []));
  for (const id of imageIds) {
    const image = await repo.getImage(id);
    if (image) images[id] = await blobToDataUrl(image);
  }

  const createdAt = new Date().toISOString();
  const payload: BackupPayloadV1 = {
    format: 'two-little-tickets-data',
    version: 1,
    createdAt,
    space,
    tickets,
    images,
  };
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveBackupKey(passphrase, salt);
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: asBuffer(iv) },
    key,
    asBuffer(plaintext),
  );
  const envelope: BackupFileV1 = {
    format: 'two-little-tickets-backup',
    version: 1,
    salt: toBase64Url(salt),
    iv: toBase64Url(iv),
    ciphertext: toBase64Url(new Uint8Array(encrypted)),
    createdAt,
  };
  const backup = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
  if (backup.size > MAX_BACKUP_FILE_BYTES) {
    throw new BackupError('encrypted backup is too large to import safely', 'BACKUP_EXPORT_TOO_LARGE');
  }
  return backup;
}

export async function inspectBackup(backup: Blob, passphrase: string): Promise<BackupPreview> {
  const { envelope, payload } = await decryptAndValidate(backup, passphrase);
  return {
    spaceName: payload.space.spaceName,
    ticketCount: payload.tickets.length,
    createdAt: envelope.createdAt,
  };
}

export async function importBackup(
  repo: BackupRepositoryContract,
  backup: Blob,
  passphrase: string,
): Promise<BackupImportResult> {
  const { payload } = await decryptAndValidate(backup, passphrase);
  const images = new Map<string, Blob>();
  for (const [id, dataUrl] of Object.entries(payload.images)) images.set(id, dataUrlToBlob(dataUrl));
  const snapshot: BackupSnapshot = { space: payload.space, tickets: payload.tickets, images };

  if (typeof repo.importBackupSnapshot !== 'function') {
    throw new BackupError('repository does not support atomic backup import', 'BACKUP_ATOMIC_REQUIRED');
  }
  try {
    return await repo.importBackupSnapshot(snapshot);
  } catch (error) {
    if (error instanceof Error && error.message === 'backup belongs to a different couple space') {
      throw new BackupError(error.message, 'BACKUP_SPACE_MISMATCH');
    }
    throw error;
  }
}

async function decryptAndValidate(
  backup: Blob,
  passphrase: string,
): Promise<{ envelope: BackupFileV1; payload: BackupPayloadV1 }> {
  if (backup.size > MAX_BACKUP_FILE_BYTES) {
    throw new BackupError('backup file is too large', 'BACKUP_TOO_LARGE');
  }
  requirePassphrase(passphrase);
  let parsed: unknown;
  try {
    parsed = JSON.parse(await backup.text());
  } catch {
    throw invalidBackup();
  }
  const envelope = validateEnvelope(parsed);
  const salt = decodeEnvelopeBytes(envelope.salt, 16);
  const iv = decodeEnvelopeBytes(envelope.iv, 12);
  const ciphertext = decodeEnvelopeBytes(envelope.ciphertext);
  if (ciphertext.length < 16) throw invalidBackup();

  let plaintext: ArrayBuffer;
  try {
    const key = await deriveBackupKey(passphrase, salt);
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: asBuffer(iv) },
      key,
      asBuffer(ciphertext),
    );
  } catch {
    throw new BackupError('backup password is wrong or the encrypted file is damaged', 'BACKUP_DECRYPTION');
  }

  let payload: unknown;
  try {
    payload = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(plaintext));
  } catch {
    throw invalidBackup();
  }
  const validated = validatePayload(payload);
  if (validated.createdAt !== envelope.createdAt) throw invalidBackup();
  return { envelope, payload: validated };
}

function validateEnvelope(value: unknown): BackupFileV1 {
  if (!isRecord(value) || !hasOnlyKeys(value, ENVELOPE_KEYS)
    || value.format !== 'two-little-tickets-backup' || value.version !== 1
    || !isString(value.salt) || !isString(value.iv) || !isString(value.ciphertext)
    || !isIsoInstant(value.createdAt)) {
    throw invalidBackup();
  }
  return value as unknown as BackupFileV1;
}

function validatePayload(value: unknown): BackupPayloadV1 {
  if (!isRecord(value) || !hasOnlyKeys(value, PAYLOAD_KEYS)
    || value.format !== 'two-little-tickets-data' || value.version !== 1
    || !isIsoInstant(value.createdAt) || !Array.isArray(value.tickets) || !isRecord(value.images)) {
    throw invalidBackup();
  }
  validateSpace(value.space);
  const seenTicketIds = new Set<string>();
  for (const ticket of value.tickets) {
    validateTicket(ticket);
    if (ticket.coupleId !== value.space.coupleId) throw invalidBackup();
    if (seenTicketIds.has(ticket.id)) throw invalidBackup();
    seenTicketIds.add(ticket.id);
  }
  for (const [id, dataUrl] of Object.entries(value.images)) {
    if (!id || !isString(dataUrl)) throw invalidBackup();
    validateDataUrl(dataUrl);
  }
  return value as unknown as BackupPayloadV1;
}

function validateSpace(value: unknown): asserts value is CoupleSpace {
  if (!isRecord(value) || !hasAllowedKeys(value, SPACE_KEYS) || !hasRequiredKeys(value, REQUIRED_SPACE_KEYS)
    || !nonEmptyString(value.coupleId) || !MEMBERS.has(String(value.memberId))
    || !nonEmptyString(value.spaceName) || !isString(value.selfName) || !isString(value.selfAvatar)
    || !isString(value.partnerName) || !isString(value.partnerAvatar) || !nonEmptyString(value.key)
    || !isNullableDate(value.anniversary) || !isIsoInstant(value.createdAt)
    || !(value.peerReadyAt === undefined || isIsoInstant(value.peerReadyAt))) {
    throw invalidBackup();
  }
}

function validateTicket(value: unknown): asserts value is Ticket {
  if (!isRecord(value) || !hasOnlyKeys(value, TICKET_KEYS)
    || !TYPES.has(String(value.type)) || !isString(value.title) || !isString(value.description)
    || !isString(value.outcome) || !isNullableDate(value.dueDate) || !IMPORTANCE.has(String(value.importance))
    || !Array.isArray(value.tags) || !value.tags.every(isString) || !isString(value.signature)
    || !(value.imageId === null || isString(value.imageId)) || !nonEmptyString(value.id)
    || !nonEmptyString(value.coupleId) || !MEMBERS.has(String(value.creatorId))
    || !MEMBERS.has(String(value.recipientId)) || !isIsoInstant(value.createdAt)
    || !isIsoInstant(value.updatedAt) || !STATES.has(String(value.state))
    || !nonEmptyString(value.headEventId) || !Array.isArray(value.events)) {
    throw invalidBackup();
  }
  for (const event of value.events) validateEvent(event);
  try {
    validateTicketGraph(value as unknown as Ticket);
  } catch {
    throw invalidBackup();
  }
}

function validateEvent(value: unknown): asserts value is TicketEvent {
  if (!isRecord(value) || !hasAllowedKeys(value, EVENT_KEYS)
    || !nonEmptyString(value.id) || !Array.isArray(value.parentIds) || !value.parentIds.every(nonEmptyString)
    || !MEMBERS.has(String(value.actor)) || !ACTIONS.has(String(value.action)) || !isIsoInstant(value.at)
    || !(value.note === undefined || isString(value.note)) || !STATES.has(String(value.state))) {
    throw invalidBackup();
  }
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return `data:${blob.type || 'application/octet-stream'};base64,${toBase64(bytes)}`;
}

function dataUrlToBlob(value: string): Blob {
  const { type, encoded } = validateDataUrl(value);
  let binary: string;
  try {
    binary = atob(encoded);
  } catch {
    throw invalidBackup();
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type });
}

function validateDataUrl(value: string): { type: string; encoded: string } {
  const match = /^data:([a-z0-9.+-]+\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/]*={0,2})$/iu.exec(value);
  if (!match || match[2]!.length % 4 !== 0) throw invalidBackup();
  return { type: match[1]!, encoded: match[2]! };
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 32 * 1024) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 32 * 1024));
  }
  return btoa(binary);
}

function decodeEnvelopeBytes(value: string, expectedLength?: number): Uint8Array {
  let bytes: Uint8Array;
  try {
    bytes = fromBase64Url(value);
  } catch {
    throw invalidBackup();
  }
  if (expectedLength !== undefined && bytes.length !== expectedLength) throw invalidBackup();
  return bytes;
}

function requirePassphrase(passphrase: string): void {
  if (passphrase.trim().length === 0) {
    throw new BackupError('backup password must not be empty', 'BACKUP_PASSPHRASE');
  }
}

function invalidBackup(): BackupError {
  return new BackupError('backup file is invalid', 'BACKUP_INVALID');
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  const keys = Object.keys(value);
  return keys.length === allowed.length && keys.every((key) => allowed.includes(key));
}

function hasAllowedKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

function hasRequiredKeys(value: Record<string, unknown>, required: readonly string[]): boolean {
  return required.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function nonEmptyString(value: unknown): value is string {
  return isString(value) && value.length > 0;
}

function isIsoInstant(value: unknown): value is string {
  if (!isString(value)) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function isNullableDate(value: unknown): value is string | null {
  return value === null || (isString(value) && /^\d{4}-\d{2}-\d{2}$/u.test(value));
}

function asBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
